import React, { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as Portal from '@radix-ui/react-portal';
import { useDispatch } from 'react-redux';
import { addNote, resetActiveEntityId } from '../../redux/notesetting/notesSettingsSlice';
import ExternalNoteForm from './ExternalNoteForm';
import { Modal } from 'antd';

const ExternalCreateNoteForm = ({ pendingNote, setPendingNote, isAdmin, imageData, workflowContactId }) => {
  const { targetSelector, coords } = pendingNote || {};
  const dispatch = useDispatch();

  const [isDirty, setIsDirty] = useState(false);
  const [forceOpen, setForceOpen] = useState(!!pendingNote);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Detect outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.create-note-content')) return;
      const clickedOutsidePopover = popoverRef.current && !popoverRef.current.contains(event.target);
      const clickedOutsideTrigger = triggerRef.current && !triggerRef.current.contains(event.target);
      if (clickedOutsidePopover && clickedOutsideTrigger && isDirty && forceOpen) {
        event.preventDefault();
        event.stopPropagation();
        setShowWarningModal(true);
      }
    };

    if (forceOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }
  }, [isDirty, forceOpen]);

  const handlePopoverChange = (isOpen) => {
    if (!isOpen && isDirty) {
      setShowWarningModal(true);
      return;
    }
    setForceOpen(isOpen);
    if (!isOpen) setPendingNote(null);
  };

  const handleDiscardConfirm = () => {
    setIsDirty(false);
    setForceOpen(false);
    setShowWarningModal(false);
    setPendingNote(null);
  };

  const handleDiscardCancel = () => setShowWarningModal(false);

  const handleSubmit = (text) => {
    dispatch(addNote({ targetSelector, coords, text }));
    setIsDirty(false);
    setForceOpen(false);
    setPendingNote(null);
  };

  return (
    <>
      <Popover.Root open={forceOpen} onOpenChange={handlePopoverChange} modal={true}>
        <Portal.Root
          className="note"
          container={document.querySelector(targetSelector)}
        >
          <Popover.Trigger
            ref={triggerRef}
            className="create-note-form pin"
            style={{
              transform: `translate3d(${coords?.x.size + coords?.x.unit}, ${coords?.y.size + coords?.y.unit}, 0)`,
            }}
          />
        </Portal.Root>

        <Popover.Portal>
          <Popover.Content
            ref={popoverRef}
            className="create-note-content"
            sideOffset={20}
            side="right"
            onInteractOutside={(e) => {
              if (isDirty) {
                e.preventDefault();
                setShowWarningModal(true);
              }
            }}
          >
            <ExternalNoteForm
              pendingNote={pendingNote}
              isAdmin={isAdmin}
              imageData={imageData}
              workflowContactId={workflowContactId}
              onDirtyChange={setIsDirty}
              onSubmit={handleSubmit}
              onCommentAdded={() => {
                setForceOpen(false);
                dispatch(resetActiveEntityId());
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Modal
        title="Discard Note"
        open={showWarningModal}
        onOk={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
        okText="Continue"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>The note will be discarded. Do you want to continue?</p>
      </Modal>
    </>
  );
};

export default ExternalCreateNoteForm;
